import { resolveUsageTab, USAGE_TABS, type UsageTabId } from "./usageSections";

import { AppTopBarActions } from "../appTopBarActionSlot";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import { ProviderUsageTab } from "./ProviderUsageTab";
import { UsageEstimateTab } from "./UsageEstimateTab";

interface UsagePageProps {
  readonly activeTab: UsageTabId;
  readonly onTabChange: (tab: UsageTabId) => void;
}

/**
 * Connected subscriptions is what usage opens on, and it already rolls every
 * account's usage together, so it carries no section name of its own. The one
 * detour worth a control is estimated value, reached through a button in the
 * top bar rather than a breadcrumb menu naming a section that was the whole
 * page anyway.
 */
export function UsagePage({ activeTab, onTabChange }: UsagePageProps) {
  const section = resolveUsageTab(activeTab);

  return (
    <ScrollArea className="min-h-0 flex-1">
      <AppTopBarActions>
        <UsageSectionSwitchButton activeTab={section} onTabChange={onTabChange} />
      </AppTopBarActions>
      {/* t3code's `wide` page frame: 5xl rather than 6xl so the chart and the
          breakdown table are not stretched across a near-empty row, and `pb-12`
          so the last table row is not flush against the scroll area's edge. */}
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-5 pt-6 pb-12 sm:px-6">
        <UsageSectionPanel activeTab={section} />
      </div>
    </ScrollArea>
  );
}

/**
 * The one section that is showing.
 *
 * Separated from the page so adding a section is one arm here rather than
 * another condition threaded through the page's own body.
 */
function UsageSectionPanel({ activeTab }: { readonly activeTab: UsageTabId }) {
  switch (activeTab) {
    case "provider-usage": {
      return <ProviderUsageTab />;
    }
    case "estimate": {
      return <UsageEstimateTab />;
    }
  }
}

/**
 * Names wherever the other section is, rather than the one you are on.
 *
 * On connected subscriptions this reads "Estimated value"; on estimated value
 * it reads "Connected subscriptions", so a single button covers both
 * directions without a menu that has to list a section you are already
 * looking at.
 */
function UsageSectionSwitchButton({
  activeTab,
  onTabChange,
}: {
  readonly activeTab: UsageTabId;
  readonly onTabChange: (tab: UsageTabId) => void;
}) {
  const target = USAGE_TABS.find((tab) => tab.id !== activeTab);
  if (target === undefined) return null;

  return (
    <Button onClick={() => onTabChange(target.id)} size="sm" variant="outline">
      {target.label}
    </Button>
  );
}
